/**
 * Cases Service Layer
 *
 * Provides a clean, type-safe interface for case/project operations.
 * Used by React Query hooks for data fetching and mutations.
 */

import { apiClient } from './api.client';
import type { CasesResponse, CaseFilters } from '@lnr-v2/types';

/**
 * Build query params from filters
 */
function buildSearchParams(filters: CaseFilters): URLSearchParams {
  const params = new URLSearchParams();

  if (filters.page) params.append('page', filters.page.toString());
  if (filters.limit) params.append('limit', filters.limit.toString());
  if (filters.search) params.append('search', filters.search);
  if (filters.categoryId) params.append('categoryId', filters.categoryId);
  if (filters.clientIndustry)
    params.append('clientIndustry', filters.clientIndustry);
  if (filters.status) params.append('status', filters.status);
  if (filters.isFeatured !== undefined)
    params.append('isFeatured', filters.isFeatured.toString());
  if (filters.sortBy) params.append('sortBy', filters.sortBy);
  if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

  // Admin always includes unpublished cases
  params.append('includeUnpublished', 'true');

  return params;
}

/**
 * Cases Service
 * All case/project-related API operations
 */
export const casesService = {
  /**
   * Get all cases with pagination and filters
   */
  async getAll(filters: CaseFilters): Promise<CasesResponse> {
    const params = buildSearchParams(filters);
    return apiClient.get(`cases?${params.toString()}`).json<CasesResponse>();
  },

  /**
   * Get a single case by ID
   */
  async getById(id: string): Promise<CasesResponse['cases'][0]> {
    return apiClient.get(`cases/${id}`).json<CasesResponse['cases'][0]>();
  },

  /**
   * Create a new case
   */
  async create(
    data: Partial<CasesResponse['cases'][0]>
  ): Promise<CasesResponse['cases'][0]> {
    return apiClient
      .post('cases', { json: data })
      .json<CasesResponse['cases'][0]>();
  },

  /**
   * Update an existing case
   */
  async update(
    id: string,
    data: Partial<CasesResponse['cases'][0]>
  ): Promise<CasesResponse['cases'][0]> {
    return apiClient
      .put(`cases/${id}`, { json: data })
      .json<CasesResponse['cases'][0]>();
  },

  /**
   * Delete a case
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`cases/${id}`);
  },

  /**
   * Reorder cases by updating their sortOrder
   */
  async reorder(
    updates: Array<{ id: string; sortOrder: number }>
  ): Promise<void> {
    await apiClient.put('cases/reorder', { json: { updates } });
  },
};
